import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import {
  getAllDataPoints,
  syncOneDatapoint,
  deleteDatapointAndFiles
} from "../../indexDB/utils/datapoint";
import { getDatasetByUUID } from "../../indexDB/utils/dataset";
import { getProjectByUUID } from "../../indexDB/utils/project";
import { useNavigate } from "react-router-dom";
import { db } from "../../indexDB/model";
import { handleApiError } from "../errorBoundary/errorUtils";
import { useErrorBoundary } from "react-error-boundary";
import useNetworkStatus from "../../hooks/useNetworStatus";

/**
 * Component for managing and syncing unsynced datapoints.
 *
 * @component
 *
 * @param {Object} props - Component properties.
 * @param {Function} props.handleLogout - Logout function.
 * @returns {JSX.Element} - UnsyncDatapoints component.
 */
const UnsyncDatapoints = ({ setConfirmDialog = () => { },bgsyncing, handleLogout, homePageCounts}) => {
  const { showBoundary } = useErrorBoundary();
  const isOnline = useNetworkStatus()

  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const rowsPerPage = Math.floor(window.innerHeight / 100);

  /**
   * Fetches unsynced datapoints from IndexedDB and updates state.
   *
   * @returns {void}
   */
  const fetchEntriesFromIndexedDB = async () => {
    try {
      const entries = await getAllDataPoints(handleLogout);
      const entriesWithData = await Promise.all(
        entries.map(async (entry) => {
          const dataset = await getDatasetByUUID(entry.datasetID);
          const project = dataset
            ? await getProjectByUUID(dataset.projectID)
            : null;

          const formData = entry.formData;

          const showLabels = dataset?.schema?.show_labels || [];
          let showLabel1, showLabel2;

          if (showLabels.length > 0) {
            const pathParts1 = showLabels[0].split(".");
            showLabel1 = traverseFormData(formData, pathParts1);
          }

          if (showLabels.length > 1) {
            const pathParts2 = showLabels[1].split(".");
            showLabel2 = traverseFormData(formData, pathParts2);
          }

          return {
            ...entry,
            datasetName: dataset ? dataset.name : null,
            projectName: project ? project.name : null,
            showLabel1: showLabel1,
            showLabel2: showLabel2,
          };
        })
      );
      setEntries(entriesWithData);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error fetching entries from IndexedDB:", error);
    }
  };

  /**
   * Traverses the formData object using path parts.
   *
   * @param {Object} formData - The formData object.
   * @param {string[]} pathParts - Array of path parts.
   * @returns {*} - The value found at the specified path.
   */
  const traverseFormData = (formData, pathParts) => {
    return pathParts.reduce(
      (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : undefined),
      formData
    );
  };

  /**
   * Removes an entry from the state by ID.
   *
   * @param {number} id - ID of the datapoint to be removed from the list.
   * @returns {void}
   */
  const removeEntryById = (id) => {
    setEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === id ? { ...entry, isRemoving: true } : entry
      )
    );

    setTimeout(() => {
      setEntries((prevEntries) =>
        prevEntries.filter((entry) => entry.id !== id)
      );
    }, 500);
  };

  useEffect(() => {
    fetchEntriesFromIndexedDB();
  }, [homePageCounts]);
  /**
   * Handles syncing all unsynced datapoints.
   *
   * @returns {void}
   */
  const handleSyncAll = async () => {
    if (!isOnline) {
      setConfirmDialog({ open: true, text: "There is no network connection, so we couldn't fetch the latest forms.", type: "info", buttonText: "Close"})
      return
    }
    try {
      const datapoints = await db.datapoints.toArray();
      for (const datapoint of datapoints) {
        await handleSyncOne(datapoint.id);
      }
    } catch (error) {
      console.log("Error syncing formdata/datapoints to online:", error);
      showBoundary(error);
    }
  };

  /**
   * Handles syncing a single unsynced datapoint by ID.
   *
   * @param {number} id - ID of the datapoint to sync.
   * @returns {void}
   */
  const handleSyncOne = async (id) => {
    console.log(isOnline)
    if (!isOnline) {
      setConfirmDialog({ open: true, text: "There is no network connection, so we couldn't fetch the latest forms.", type: "info", buttonText: "Close"})
      return
    }
    try {
      setSyncing(true);
      const response = await syncOneDatapoint(id, handleLogout);
      if (response === true) {
        removeEntryById(id);
      } 
    } catch (error) {
      showBoundary(error);
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteOne = async (id) =>{
    try{
      const response = await deleteDatapointAndFiles(id);
      console.log("response:", response)
      if (response === true) {
        removeEntryById(id);
      } else {
        throw new Error("Deletion failed");
      }

    }catch (error) {
      console.log("Error deleting id:", id, error);
    }
  }

  /**
   * Handles clicking on an item and navigates to view the form.
   *
   * @param {string} datasetID - ID of the dataset.
   * @param {Object} formData - Form data object.
   * @param {number} id - ID of the datapoint.
   * @returns {void}
   */
  const handleItemClick = async (datasetID,draftId, formData, id) => {
    const files = await db.fileData.where("datapointID").equals(id).toArray();
    navigate("/view-form", {
      state: { datasetID,draftId, formData, files, id, status: "offline" },
    });
  };

  return (
    <div>
      <Container>
        <Container style={{ padding: 4, marginTop: 16 }}>
          <div
            style={{ display: "flex", justifyContent: "end", marginBottom: 16 }}
          >
            <Button
              variant="contained"
              color="secondary"
              disabled={syncing||bgsyncing}
              onClick={handleSyncAll}
            >
              {bgsyncing ? (
                <>
                <CircularProgress size={12} color="inherit" sx={{marginRight:'10px'}}/> Syncing
                </>
              ) : (
                'Sync All'
              )}
            </Button>
          </div>
          <Divider />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            List of Unsynced Forms
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Dataset Name</TableCell>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Identifier 1</TableCell>
                  <TableCell>Identifier 2</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: rowsPerPage }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 6 }).map((_, colIndex) => (
                          <TableCell key={colIndex}>
                            <Skeleton animation="wave" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : entries?.map((entry, index) => (
                      <TableRow
                        key={index}
                        hover
                        style={
                          entry.isRemoving
                            ? { opacity: 0, transition: "opacity 0.5s" }
                            : null
                        }
                      >
                        <TableCell>{entry.draft_id || "-----" }</TableCell>
                        <TableCell>{entry.datasetName || "-----"}</TableCell>
                        <TableCell>{entry.projectName || "-----"}</TableCell>
                        <TableCell>{entry.showLabel1 || "-----"}</TableCell>
                        <TableCell>{entry.showLabel2 || "-----"}</TableCell>
                        <TableCell>
                          <ButtonGroup>
                            <Button
                              onClick={() =>
                                handleItemClick(
                                  entry.datasetID,
                                  entry.draft_id,
                                  entry.formData,
                                  entry.id
                                )
                              }
                            >
                              View
                            </Button>
                            <Button
                              disabled={syncing||bgsyncing || entry?.syncing}
                              onClick={() => handleSyncOne(entry.id)}
                            >
                              Sync
                            </Button>
                            <Button
                              disabled={syncing||bgsyncing || entry?.syncing}
                              onClick={() => handleDeleteOne(entry.id)}
                            >
                              Delete
                            </Button>
                            
                          </ButtonGroup>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Container>

      {/* Disabling it for AGRI-GRAIN */}
      <Dialog open={false && syncing}> 
        <DialogTitle sx={{ textAlign: "center" }}>
          Syncing in Progress
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress size="64px" sx={{ my: 2 }} />
          <Typography variant="body1" align="center">
            Your data is being synced. Please sit tight and don't close the
            application.
          </Typography>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnsyncDatapoints;
